import { useState, useEffect, useRef } from "react";
import { ModalLayout } from "./ModalLayout";
import {
  promptLibrary,
  PromptLibraryItem,
} from "../../data/promptLibrary";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { LuChevronLeft, LuChevronDown, LuCopy, LuCheck } from "react-icons/lu";
import { colors, typography, effects, components } from "../../design/tokens";
import { MarkdownContent } from "./MarkdownContent";

interface PromptLibraryModalProps {
  onClose: () => void;
  onSelectPrompt: (promptText: string) => void;
}

export function PromptLibraryModal({
  onClose,
  onSelectPrompt,
}: PromptLibraryModalProps) {
  const isCompactView = useIsCompactView();
  const [selectedPrompt, setSelectedPrompt] =
    useState<PromptLibraryItem | null>(null);
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);

  const handlePromptSelect = (prompt: PromptLibraryItem) => {
    setSelectedPrompt(prompt);
  };

  const handleInsertPrompt = (prompt: PromptLibraryItem) => {
    onSelectPrompt(prompt.promptText);
  };

  const handleCopyPrompt = async (prompt: PromptLibraryItem) => {
    try {
      await navigator.clipboard.writeText(prompt.promptText);
      setCopiedPromptId(prompt.id);
      setTimeout(() => setCopiedPromptId(null), 2000);
    } catch (err) {
      console.error("Failed to copy prompt:", err);
    }
  };

  // Compact Layout for Mobile - Custom dropdown selector with fixed bottom action bar
  if (isCompactView) {
    // Auto-select first prompt if none selected
    const currentPrompt = selectedPrompt ?? promptLibrary[0];

    return (
      <CompactPromptLibrary
        currentPrompt={currentPrompt}
        onClose={onClose}
        onSelectPrompt={setSelectedPrompt}
        onInsertPrompt={handleInsertPrompt}
        onCopyPrompt={handleCopyPrompt}
        copiedPromptId={copiedPromptId}
      />
    );
  }

  // Desktop Layout - Click-to-select with promptText preview
  return (
    <ModalLayout onClose={onClose} className="!w-[900px] max-w-[90vw]">
      <div className="flex h-[600px] w-full">
        {/* Left Panel - Prompt List */}
        <div
          className={`w-[40%] border-r border-dark-border
          overflow-y-auto`}
        >
          <div className={`p-6 border-b border-dark-border`}>
            <h2 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}>
              Prompt Library
            </h2>
          </div>
          <div className="p-2">
            {promptLibrary.map((prompt) => (
              <button
                key={prompt.id}
                id={`prompt-library-item-${prompt.id}`}
                onClick={() => handlePromptSelect(prompt)}
                className={`w-full text-left p-4 ${effects.borderRadius.lg} mb-2 ${effects.transition.default}
                  ${
                    selectedPrompt?.id === prompt.id
                      ? "bg-dark-surface-secondary text-dark-text"
                      : "text-dark-text hover:bg-dark-surface-secondary"
                  }`}
              >
                <div>{prompt.title}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel - Prompt Preview with Description, Prompt Text, and Actions */}
        <div className="w-[60%] flex flex-col">
          {selectedPrompt ? (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="p-6 border-b border-dark-border">
                <h3 className={`${typography.size.xl} ${typography.weight.semibold} ${colors.text.primary}`}>
                  {selectedPrompt.title}
                </h3>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Description */}
                <div className="prose prose-invert max-w-none mb-6">
                  <MarkdownContent content={selectedPrompt.description} />
                </div>

                {/* Prompt Message Preview */}
                <div>
                  <p className={`text-sm ${colors.text.secondary} mb-2`}>Prompt message:</p>
                  <pre className={`text-sm ${colors.surface.secondary} p-4 ${effects.borderRadius.lg} whitespace-pre-wrap overflow-x-auto ${colors.text.secondary}`}>
                    {selectedPrompt.promptText}
                  </pre>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-dark-border flex items-center justify-end gap-3">
                <button
                  onClick={() => handleCopyPrompt(selectedPrompt)}
                  className={`p-2.5 ${effects.borderRadius.lg} border border-dark-border ${colors.text.secondary} hover:${colors.text.primary} ${effects.transition.default}`}
                  aria-label="Copy to clipboard"
                  title="Copy to clipboard"
                >
                  {copiedPromptId === selectedPrompt.id ? (
                    <LuCheck size={20} className="text-green-400" />
                  ) : (
                    <LuCopy size={20} />
                  )}
                </button>
                <button
                  onClick={() => handleInsertPrompt(selectedPrompt)}
                  className={components.button.primary}
                >
                  Insert
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className={`${colors.text.secondary} text-center`}>
                Select a prompt to preview
              </p>
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

// Compact/Mobile Prompt Library with custom dropdown
function CompactPromptLibrary({
  currentPrompt,
  onClose,
  onSelectPrompt,
  onInsertPrompt,
  onCopyPrompt,
  copiedPromptId,
}: {
  currentPrompt: PromptLibraryItem;
  onClose: () => void;
  onSelectPrompt: (prompt: PromptLibraryItem) => void;
  onInsertPrompt: (prompt: PromptLibraryItem) => void;
  onCopyPrompt: (prompt: PromptLibraryItem) => void;
  copiedPromptId: string | null;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click-outside detection for dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: colors.hex.surfaceSecondary }}
    >
      {/* Header with Back Button + Custom Dropdown */}
      <div className="flex items-center px-4 py-3 border-b border-dark-border gap-3">
        <button
          onClick={onClose}
          className={components.modal.closeButton}
          aria-label="Close"
        >
          <LuChevronLeft size={24} />
        </button>

        {/* Custom Dropdown */}
        <div className="flex-1 relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full flex items-center justify-between bg-black border border-gray-800 ${effects.borderRadius.lg} px-3 py-2 ${typography.weight.medium} ${typography.size.sm} text-white`}
          >
            <span className="truncate">{currentPrompt.title}</span>
            <LuChevronDown
              size={18}
              className={`ml-2 flex-shrink-0 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isDropdownOpen && (
            <div
              className={`absolute top-full left-0 right-0 mt-1 p-1 rounded-md bg-black text-white border border-gray-800 shadow-[0_2px_6px_rgba(0,0,0,0.18)] z-50 max-h-[300px] overflow-y-auto`}
            >
              {promptLibrary.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    onSelectPrompt(p);
                    setIsDropdownOpen(false);
                  }}
                  className={`${components.dropdown.item} ${
                    currentPrompt.id === p.id ? "bg-white/10" : ""
                  }`}
                >
                  <span className="truncate">{p.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content - Description + Prompt Message */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Description */}
        <div className="prose prose-invert max-w-none mb-6">
          <MarkdownContent content={currentPrompt.description} />
        </div>

        {/* Prompt Message Preview */}
        <div>
          <p className={`text-xs ${colors.text.secondary} mb-2`}>
            Prompt message:
          </p>
          <pre
            className={`text-sm ${colors.surface.secondary} p-3 ${effects.borderRadius.lg} whitespace-pre-wrap overflow-x-auto ${colors.text.secondary}`}
          >
            {currentPrompt.promptText}
          </pre>
        </div>
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="px-4 py-3 border-t border-dark-border flex gap-2 touch-none">
        <button
          onClick={() => onCopyPrompt(currentPrompt)}
          className={`flex-shrink-0 p-3 ${effects.borderRadius.lg} border border-dark-border ${colors.text.secondary} hover:${colors.text.primary} ${effects.transition.default}`}
          aria-label="Copy to clipboard"
        >
          {copiedPromptId === currentPrompt.id ? (
            <LuCheck size={20} className="text-green-400" />
          ) : (
            <LuCopy size={20} />
          )}
        </button>
        <button
          onClick={() => onInsertPrompt(currentPrompt)}
          className={components.button.primaryLarge + " flex-1"}
        >
          Insert
        </button>
      </div>
    </div>
  );
}
